// TitleBarCropControl — the "Crop to bar" editor row (deck-15 delta). Owns every
// STATE the control can be in; the heavy cropperjs marquee itself is lazy and
// lives in TitleBarCropStage.
//
// States:
//   • empty   — no image chosen yet: an honest hint, no dead control.
//   • closed  — an image is chosen: "Crop to bar" opens the marquee; a summary
//               of the current region + "Use whole image" reset when cropped.
//   • loading — the lazy marquee chunk is loading (Suspense fallback).
//   • open    — the marquee (aspect-locked, dimmed outside, 8 handles).
//   • error   — the image cannot be loaded (raised by the stage).
//
// A11y: real <button>s (>=44px, visible focus via the shared field ring), the
// region summary is a live region so a screen reader hears the crop change, and
// the marquee's own instructions are text — never color-only.

import React, { useState } from "react";
import styled from "styled-components";
import { fontFamily } from "../../../../theme/tokens";
import { fieldChrome, fieldFocusRing, FieldLabel } from "../../BlockEditorPane/EditorField";
import { describeCrop, isCropped, FULL_CROP } from "./barCrop";

const TitleBarCropStage = React.lazy(() => import("./TitleBarCropStage"));

const Wrap = styled.div`
  font-family: ${fontFamily};
`;
const Hint = styled.p`
  margin: 0;
  font-family: ${fontFamily};
  font-size: 12px;
  font-style: italic;
  color: ${fieldChrome.muted};
  line-height: 1.4;
`;
const Summary = styled.p`
  margin: 6px 0 0;
  font-family: ${fontFamily};
  font-size: 12px;
  color: ${fieldChrome.ink};
  line-height: 1.4;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;
const ActionButton = styled.button`
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${(p) => (p.$primary ? fieldChrome.accent : fieldChrome.border)};
  border-radius: 8px;
  background: ${(p) => (p.$primary ? "#F1E9FE" : fieldChrome.bg)};
  color: ${(p) => (p.$primary ? "#6f27d6" : fieldChrome.ink)};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  ${fieldFocusRing}
  &:hover {
    border-color: ${fieldChrome.accent};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const TitleBarCropControl = ({ src, crop, onChange }) => {
  const [open, setOpen] = useState(false);
  const cropped = isCropped(crop);

  if (!src) {
    return (
      <Wrap data-title-bar-crop>
        <FieldLabel as="div">Crop to bar</FieldLabel>
        <Hint data-title-bar-crop-empty>
          Pick an image for the bar background first — then you can drag the
          region that fills the bar.
        </Hint>
      </Wrap>
    );
  }

  return (
    <Wrap data-title-bar-crop>
      <FieldLabel as="div">Crop to bar</FieldLabel>
      <Summary aria-live="polite" data-title-bar-crop-summary>
        {describeCrop(crop)}
      </Summary>
      <Actions>
        <ActionButton
          type="button"
          $primary
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Done cropping" : "Crop to bar"}
        </ActionButton>
        {cropped && (
          <ActionButton type="button" onClick={() => onChange(FULL_CROP)}>
            Use whole image
          </ActionButton>
        )}
      </Actions>
      {open && (
        <React.Suspense fallback={<Hint>Loading the cropping tool…</Hint>}>
          <TitleBarCropStage src={src} crop={crop} onChange={onChange} />
        </React.Suspense>
      )}
    </Wrap>
  );
};

export default TitleBarCropControl;
